import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const BrandIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M5 4h14v4.2L8.7 20H5.2l9.1-10.4H5V4z" />
  </svg>
);

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUserData } = useAuth();
  const [message, setMessage] = useState('Finalizando seu acesso...');
  const [phaseLabel, setPhaseLabel] = useState('Liberando acesso');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      toast.error('Não encontramos a sessão do checkout.');
      navigate('/login', { replace: true });
      return;
    }

    const finalize = async () => {
      try {
        setPhaseLabel('Confirmando pagamento');
        const response = await fetch(`${API}/public/billing/complete-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Não foi possível finalizar seu acesso.');
        }
        if (data.user) {
          setUserData(data.user);
        }
        setPhaseLabel('Conta pronta');
        setMessage('Tudo certo. Entrando na sua conta...');
        toast.success('Teste iniciado com sucesso!');
        navigate(data.user?.requires_password_setup ? '/app/setup-password' : '/app', { replace: true });
      } catch (error) {
        toast.error(error.message || 'Erro ao ativar sua conta');
        navigate('/login', { replace: true });
      }
    };

    finalize();
  }, [navigate, searchParams, setUserData]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[radial-gradient(circle_at_top,_#eef6ff,_#fafafa_55%,_#ffffff)] px-6 overflow-hidden">
      <style>{`
        @keyframes zaptOrbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes zaptPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.16); }
          50% { transform: scale(1.03); box-shadow: 0 0 0 18px rgba(14, 165, 233, 0); }
        }
      `}</style>

      <div className="max-w-md text-center relative">
        <div className="relative mx-auto mb-8 h-28 w-28">
          <div
            className="absolute inset-0 rounded-full border border-sky-200/70"
            style={{ animation: 'zaptOrbit 7s linear infinite' }}
          >
            <span className="absolute -top-1 left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-emerald-400" />
          </div>
          <div className="absolute inset-3 rounded-full border border-violet-200/80 border-dashed" style={{ animation: 'zaptOrbit 10s linear infinite reverse' }} />
          <div
            className="absolute inset-5 rounded-[28px] bg-gradient-to-br from-sky-500 via-blue-500 to-violet-600 flex items-center justify-center text-white"
            style={{ animation: 'zaptPulse 2.2s ease-in-out infinite' }}
          >
            <BrandIcon className="h-9 w-9" />
          </div>
        </div>

        <div className="mb-5 flex flex-col items-center gap-3">
          <div className="inline-flex items-center gap-3 rounded-full border border-sky-100 bg-white/90 px-4 py-2 shadow-sm shadow-sky-100/70">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-blue-500 to-violet-600 text-white">
              <BrandIcon className="h-5 w-5" />
            </div>
            <div className="text-left">
              <p className="text-lg font-semibold leading-none text-zinc-900">Zapt Resumo</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-700">
                {phaseLabel}
              </p>
            </div>
          </div>
        </div>

        <h1 className="text-3xl font-semibold text-zinc-900">Liberando seu acesso</h1>
        <p className="text-zinc-500 mt-3 leading-relaxed">{message}</p>

        <div className="mt-6 flex items-center justify-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-sky-500 animate-bounce [animation-delay:-0.2s]" />
          <span className="h-2.5 w-2.5 rounded-full bg-violet-500 animate-bounce [animation-delay:-0.1s]" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-bounce" />
        </div>

        <p className="mt-5 text-sm text-zinc-400">
          Estamos preparando sua conta para o primeiro acesso.
        </p>
      </div>
    </div>
  );
}
